import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { StickyNote, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { customersApi } from "@/lib/admin-api/crm";
import type { CustomerNote } from "@/lib/admin-api/crm";
import { ApiError } from "@/lib/api-error-handler";

// ---------------------------------------------------------------------------
// NotesPanel — live notes with add / delete
// ---------------------------------------------------------------------------

export function NotesPanel({ customerId }: { readonly customerId: string }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");

  const { data: notes = [] } = useQuery<readonly CustomerNote[]>({
    queryKey: ["admin", "customer-notes", customerId],
    queryFn: () => customersApi.notes.list(customerId),
    staleTime: 10_000,
  });

  const addMutation = useMutation({
    mutationFn: (text: string) => customersApi.notes.add(customerId, text),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "customer-notes", customerId] });
      setDraft("");
      toast.success("Note ajoutée");
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.toHumanMessage() : "Impossible d'ajouter la note"),
  });

  const removeMutation = useMutation({
    mutationFn: (noteId: string) => customersApi.notes.remove(customerId, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "customer-notes", customerId] });
      toast.success("Note supprimée");
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.toHumanMessage() : "Impossible de supprimer"),
  });

  return (
    <div className="space-y-3 rounded-xl border border-stone-200 bg-white p-5">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-stone-700">
        <StickyNote className="h-4 w-4 text-stone-400" />
        Notes
      </h3>
      {notes.length === 0 ? (
        <p className="text-xs italic text-stone-400">Aucune note</p>
      ) : (
        <ul className="space-y-2">
          {notes.map((n) => (
            <li key={n.id} className="flex items-start gap-2 border-l-2 border-amber-200 pl-3">
              <div className="flex-1">
                <p className="text-sm text-stone-700 whitespace-pre-wrap">{n.text}</p>
                <p className="mt-0.5 text-[10px] uppercase tracking-wide text-stone-400">
                  {n.admin?.full_name ?? "Admin"} · {new Date(n.created_at).toLocaleString("fr-SN")}
                </p>
              </div>
              <button
                type="button"
                disabled={removeMutation.isPending}
                onClick={() => {
                  if (confirm("Supprimer cette note ?")) {
                    removeMutation.mutate(n.id);
                  }
                }}
                aria-label="Supprimer la note"
                className="text-stone-400 hover:text-red-600"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const trimmed = draft.trim();
          if (trimmed.length > 0 && trimmed.length <= 2000) {
            addMutation.mutate(trimmed);
          }
        }}
        className="flex gap-2"
      >
        <textarea
          rows={2}
          maxLength={2000}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Ajouter une note interne…"
          className="flex-1 rounded border border-stone-200 bg-white px-2 py-1 text-xs focus:border-amber-400 focus:outline-none"
        />
        <button
          type="submit"
          disabled={addMutation.isPending || draft.trim().length === 0}
          className="inline-flex items-center gap-1 self-start rounded border border-stone-200 bg-stone-50 px-2 py-1 text-xs font-medium text-stone-700 hover:bg-stone-100 disabled:opacity-50"
        >
          <Plus className="h-3 w-3" />
          Ajouter
        </button>
      </form>
    </div>
  );
}
